import axios from 'axios';

// Backend routes verify a real bearer token now — mirrors getDashboardAuthHeaders()
// in rcmService.js so Finance View reads stay authenticated the same way.
function getAuthHeaders() {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Compose Finance View reads (Gold + AR + RA + Denials). Inject shell URL helpers.
 * Do not clone this in AgenticRCM or Agency.
 */
export function createFinanceViewService({
  getServiceApiUrl,
  normalizeReportingPeriod,
  consumerService = 'agenticrcm',
}) {
  async function postFacade(dataset, organizationId, period, extraFilters = {}) {
    const base = getServiceApiUrl('ANALYTICS_FACADE');
    if (!base || !organizationId) return null;
    const range = normalizeReportingPeriod(period);
    const filters = { ...extraFilters };
    const response = await axios.post(
      `${base}/api/v1/analytics/query`,
      {
        schema_version: '1.0',
        organization_id: organizationId,
        consumer_service: consumerService,
        dataset,
        time_range: { start: range.startDate, end: range.endDate },
        filters: Object.keys(filters).length ? filters : undefined,
      },
      { timeout: 8000, headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
    );
    return response.data?.data?.[0] || null;
  }

  async function getJson(serviceName, path, params) {
    const base = getServiceApiUrl(serviceName);
    if (!base) return null;
    try {
      const response = await axios.get(`${base}${path}`, {
        params,
        timeout: 5000,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
      return response.data;
    } catch {
      return null;
    }
  }

  return {
    async getCaptureConfidence(organizationId, period) {
      try {
        return await postFacade('gold_capture_confidence', organizationId, period);
      } catch {
        return null;
      }
    },

    async getUnmatched(organizationId, period, { payerId } = {}) {
      try {
        const extra = payerId ? { payer_ids: [payerId] } : {};
        return await postFacade('gold_finance_unmatched', organizationId, period, extra);
      } catch {
        return null;
      }
    },

    async getDollarsAtRisk(organizationId, { wrapEnabled = false } = {}) {
      const [underpay, eraAnalytics, processEff] = await Promise.all([
        getJson('AR_POSTING', '/api/v1/dashboard/underpayment-statistics', {
          organization_id: organizationId,
        }),
        getJson('REMITTANCE_ADVICE', '/api/v1/eobs/analytics', {
          organization_id: organizationId,
        }),
        getJson('DENIAL_MANAGEMENT', '/api/v1/analytics/advanced/process-efficiency', {
          organization_id: organizationId,
        }),
      ]);

      const underpayRow = underpay?.total_amount != null ? underpay : underpay?.data || underpay;
      const era = eraAnalytics?.by_carc ? eraAnalytics : eraAnalytics?.data || eraAnalytics;
      const timely = processEff?.timely_filing_risk || processEff?.data?.timely_filing_risk || {};
      const underpayAmount = Number(
        underpayRow?.total_amount ?? underpayRow?.total_variance ?? 0
      );
      const timelyCount =
        Number(timely.high_risk_count || 0) +
        Number(timely.medium_risk_count || 0) +
        Number(timely.low_risk_count || 0);

      const wrap = wrapEnabled
        ? {
            amount: null,
            label: 'Wrap / supplemental miss',
            included: true,
            note: 'No wrap $ SSOT on this path yet',
          }
        : {
            amount: 0,
            label: 'Wrap / supplemental miss',
            included: false,
            note: 'Omitted — wrap not enabled for this org',
          };

      const carcRows = Array.isArray(era?.by_carc)
        ? [...era.by_carc]
            .sort((a, b) => Number(b.total_amount || 0) - Number(a.total_amount || 0))
            .slice(0, 3)
        : [];

      return {
        parts: [
          {
            id: 'underpay',
            label: 'Underpayments',
            ssot: 'AR Posting',
            amount: underpayAmount,
            unit: 'usd',
            included: true,
          },
          {
            id: 'timely',
            label: 'Timely filing',
            ssot: 'Denial Management',
            amount: timelyCount,
            unit: 'count',
            included: true,
            note: 'Count of at-risk denials — not dollars',
          },
          {
            id: 'wrap',
            ...wrap,
            ssot: 'RA / AR wrap',
            unit: 'usd',
          },
        ],
        topCarcByDollar: carcRows.map((row) => ({
          carc: row.carc_code,
          amount: Number(row.total_amount || 0),
          lines: row.line_count,
        })),
      };
    },

    composeAging({ daysInArKpi, patientAging } = {}) {
      return {
        payer: {
          label: 'Payer outstanding (AR)',
          daysInAr: daysInArKpi?.days_in_ar ?? null,
          outstanding: daysInArKpi?.outstanding_ar ?? null,
          formula: daysInArKpi?.formula || 'AR 90-day outstanding / avg daily billed',
        },
        patient: {
          label: 'Patient buckets (Patient Account)',
          buckets: patientAging?.buckets || null,
          note: patientAging?.buckets
            ? null
            : 'Patient aging not composed until Patient Billing exposes buckets',
        },
      };
    },
  };
}
